import { CloseIcon, FocusTrap, TinyTitleBold, theme } from '@kvdbil/components';
import { ReactNode, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { createGlobalStyle } from 'styled-components';

const MODAL_EXIT_MS = 400;

const BodyStyleInjector = createGlobalStyle`
  body {
    overflow: hidden;

    ${
      typeof window !== 'undefined' &&
      window.innerWidth > 1024 &&
      `
        padding-right: 15px !important;
    `
    }
  }
`;

const Container = styled.div<{ $isVisible: boolean }>`
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  z-index: 200;
  pointer-events: none;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  transition: opacity 0.25s ease-in-out;
`;

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Background = styled.div`
  z-index: -1;
  position: absolute;
  width: 100%;
  height: 100%;
  background-color: rgba(34, 34, 34, 0.3);
  pointer-events: auto;
`;

const Panel = styled.div`
  position: relative;
  overflow-y: auto;

  padding: 1rem;
  background-color: white;
  border-radius: 3px;
  box-shadow:
    0px 24px 38px rgba(0, 0, 0, 0.1),
    0px 9px 46px rgba(0, 0, 0, 0.09),
    0px 11px 15px rgba(0, 0, 0, 0.15);

  margin: 1rem;
  width: 80vw;
  max-width: 512px;
  pointer-events: auto;
`;

const CloseContainer = styled.button`
  border: none;
  background: none;
  padding: 0;
  margin: 0;
  display: flex;
  justify-self: flex-end;
  cursor: pointer;
  font: inherit;
  color: inherit;

  &:focus-visible {
    outline: revert;
  }

  svg {
    font-size: 1.5rem;
  }
`;

const Title = styled(TinyTitleBold)`
  font-weight: ${theme.typography.fontAlternativeRegularWeight};
  margin: 0;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  overflow-x: hidden;
`;

type AppDialogFocusTrapProps = {
  autoFocusOnOpen?: 'firstElem' | 'firstInput' | 'none';
  restoreFocusOnClose?: boolean;
};

export interface AppDialogProps {
  title?: ReactNode;
  isOpen: boolean;
  children: ReactNode;
  actions?: ReactNode;
  withPortal?: boolean;
  withCloseIcon?: boolean;
  onClose(): void;
  focusTrapProps?: AppDialogFocusTrapProps;
  className?: string;
}

const AppDialog = ({
  isOpen,
  title,
  children,
  actions,
  onClose,
  withPortal,
  withCloseIcon = true,
  focusTrapProps = {},
  className,
}: AppDialogProps) => {
  const [mounted, setMounted] = useState(isOpen);

  useEffect(() => {
    if (isOpen) {
      let cancelled = false;
      queueMicrotask(() => {
        if (!cancelled) {
          setMounted(true);
        }
      });
      return () => {
        cancelled = true;
      };
    }

    const id = window.setTimeout(() => {
      setMounted(false);
    }, MODAL_EXIT_MS);
    return () => window.clearTimeout(id);
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose, isOpen]);

  const handleBackdropClick = () => {
    onClose();
  };

  const handleCloseClick = () => {
    onClose();
  };

  const canUseDOM = !!(
    typeof window !== 'undefined' &&
    window.document &&
    window.document.createElement
  );

  if (!mounted) {
    return null;
  }

  const tree = (
    <Container $isVisible={isOpen} className={className}>
      <Background data-testid="background" onClick={handleBackdropClick} />
      <FocusTrap pause={!isOpen} {...focusTrapProps}>
        <Wrapper>
          <Panel role="dialog" aria-modal="true" aria-labelledby="modal-title">
            {withCloseIcon && (
              <CloseContainer
                onClick={handleCloseClick}
                data-testid="close"
                type="button"
                aria-label="Close dialog"
              >
                <CloseIcon />
              </CloseContainer>
            )}
            {typeof title === 'string' ? <Title>{title}</Title> : title}
            <div>{children}</div>
            {actions ? <Actions>{actions}</Actions> : null}
          </Panel>
        </Wrapper>
      </FocusTrap>

      <BodyStyleInjector />
    </Container>
  );

  return withPortal && canUseDOM ? createPortal(tree, document.body) : tree;
};

export default AppDialog;
